// Focus traps as a stack: stacked desktop drawers (cart over filter) each
// own their trap, and closing the top one re-arms the trap beneath it -
// with a single shared handler pair, closing any drawer silently untrapped
// every still-open drawer.
/** @typedef {{ container: HTMLElement, keydown: (event: KeyboardEvent) => void, focusin: (event: FocusEvent) => void }} TrapEntry */
/** @type {TrapEntry[]} */
const trapStack = [];

/** @param {TrapEntry} entry */
function installTrap(entry) {
  document.addEventListener('keydown', entry.keydown, true);
  document.addEventListener('focusin', entry.focusin, true);
}

/** @param {TrapEntry} entry */
function uninstallTrap(entry) {
  document.removeEventListener('keydown', entry.keydown, true);
  document.removeEventListener('focusin', entry.focusin, true);
}

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Suspend (not destroy) the active trap; re-trapping the same container
  // replaces its entry instead of stacking duplicates.
  const active = trapStack[trapStack.length - 1];
  if (active) {
    uninstallTrap(active);
    if (active.container === container) trapStack.pop();
  }

  // Gather focusable elements.
  const focusable = getFocusableElements(container);
  if (!focusable.length) {
    // Nothing focusable - re-arm whatever was active and abort.
    const previous = trapStack[trapStack.length - 1];
    if (previous) installTrap(previous);
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  /** @type {TrapEntry} */
  const entry = {
    container,
    keydown: (event) => {
      if (event.key !== 'Tab') return;

      const activeEl = document.activeElement;

      // If on the last focusable and tabbing forward, go to first
      if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first?.focus();
      }
      // If on the first (or the container) and shift-tabbing, go to last
      else if (event.shiftKey && (activeEl === first || activeEl === container)) {
        event.preventDefault();
        last?.focus();
      }
    },
    focusin: (event) => {
      // If the newly focused element isn't inside the container, redirect focus back.
      if (event.target instanceof Node && !container.contains(event.target)) {
        event.stopPropagation();
        first?.focus();
      }
    },
  };

  trapStack.push(entry);
  installTrap(entry);

  // Finally, put focus where you want it.
  container.focus();
}

/**
 * Remove the top focus trap, re-arming the one beneath it (if any).
 */
export function removeTrapFocus() {
  const top = trapStack.pop();
  if (top) uninstallTrap(top);
  const previous = trapStack[trapStack.length - 1];
  if (previous) installTrap(previous);
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
